import { SuspensedView } from './components';
import { PublicRoutes } from './public-route';
import EmptyLayout from 'layouts/empty-layout';
import { MasterLayout } from 'layouts/master';
import { WarrantyLogsPage } from 'pages/warranty-logs';
import { lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

const AdvisorsCommissionsPage = lazy(() => import('pages/advisors-commissions'));
const AiSettingsPage = lazy(() => import('pages/ai-settings'));
const AppointmentsPage = lazy(() => import('pages/appointments'));
const AppraisalsPage = lazy(() => import('pages/appraisals'));
const DashboardWrapper = lazy(() => import('pages/dashboard').then((m) => ({ default: m.DashboardWrapper })));
const DeferredWorkPage = lazy(() => import('pages/deferred-work').then((m) => ({ default: m.DeferredWorkPage })));
const DeferredWorkDetailsPage = lazy(() =>
  import('pages/deferred-work-details').then((m) => ({ default: m.DeferredWorkDetailsPage }))
);
const EmployeesSettingsPage = lazy(() => import('pages/employees-settings'));
const JobsPage = lazy(() => import('pages/jobs'));
const RecallCreatePage = lazy(() => import('pages/recall-create').then((m) => ({ default: m.RecallCreatePage })));
const RecallEditPage = lazy(() => import('pages/recall-edit').then((m) => ({ default: m.RecallEditPage })));
const RecallsPage = lazy(() => import('pages/recalls').then((m) => ({ default: m.RecallsPage })));
const ServiceAdvisorsPage = lazy(() => import('pages/service-advisor'));
const SiteSettingsPage = lazy(() => import('pages/site-settings'));
const VehiclePreparationCheckPage = lazy(() =>
  import('pages/vehicle-preparation-check').then((m) => ({ default: m.VehiclePreparationCheckPage }))
);
const EditVehiclePreparationCheckPage = lazy(() =>
  import('pages/vehicle-preparation-check-edit').then((m) => ({ default: m.EditVehiclePreparationCheckPage }))
);
const VehiclePreparationLogPage = lazy(() =>
  import('pages/vehicle-preparation-log').then((m) => ({ default: m.VehiclePreparationLogPage }))
);
const VHCPage = lazy(() => import('pages/vhc'));
const VhcQuoteStats = lazy(() => import('pages/vhc-quote-stats'));
const VhcStatistics = lazy(() => import('pages/vhc-statistics'));
const WorkingHoursTrackingWrapper = lazy(() =>
  import('pages/working-hours-tracking').then((m) => ({ default: m.WorkingHoursTrackingWrapper }))
);
const WorkshopCheckInLogPage = lazy(() =>
  import('pages/workshop-check-in/log/workshop-check-in-log-page').then((m) => ({ default: m.WorkshopCheckInLogPage }))
);
const WorkshopCheckOutLogPage = lazy(() =>
  import('pages/workshop-check-out-log').then((m) => ({ default: m.WorkshopCheckOutLogPage }))
);
const ComplaintLogPage = lazy(() => import('pages/complaint-log').then((m) => ({ default: m.ComplaintLogPage })));
const ComplaintLogDetailsPage = lazy(() =>
  import('pages/complaint-log-details').then((m) => ({ default: m.ComplaintLogDetailsPage }))
);
const WorkshopCheckOutPage = lazy(() =>
  import('pages/workshop-check-out').then((m) => ({ default: m.WorkshopCheckOutPage }))
);
const WorkshopCheckOutPreviewPage = lazy(() =>
  import('pages/workshop-check-out-preview').then((m) => ({ default: m.WorkshopCheckOutPreviewPage }))
);
const WorkshopCheckInLookUpPage = lazy(() =>
  import('pages/workshop-check-in/log/workshop-check-in-lookup-page').then((m) => ({
    default: m.WorkshopCheckInLookUpPage
  }))
);
const WorkshopCheckInNewPage = lazy(() =>
  import('pages/workshop-check-in/log/workshop-check-in-new-page').then((m) => ({ default: m.WorkshopCheckInNewPage }))
);
const WorkshopCheckInViewPage = lazy(() =>
  import('pages/workshop-check-in/log/workshop-check-in-view-page').then((m) => ({
    default: m.WorkshopCheckInViewPage
  }))
);
const WorkshopPlannerPage = lazy(() => import('pages/workshop-planner'));
const EmployeesPage = lazy(() => import('pages/employees'));
const HolidaysPage = lazy(() => import('pages/holidays'));
const PurchasePlannerPage = lazy(() => import('pages/purchase-planner'));
const StockStatePage = lazy(() => import('pages/stock-state'));
const SalesPerfomancePage = lazy(() => import('pages/sales-performance'));
const TechniciansReportsPage = lazy(() => import('pages/technicians-reports'));
const MaintenanceTasksPage = lazy(() => import('pages/maintenance-tasks'));
const TrainingPlansPage = lazy(() => import('pages/training-plans'));
const VehicleAccidentLogPage = lazy(() => import('pages/vehicle-accident-log'));
const RiskAssessmentPage = lazy(() => import('pages/risk-assessment'));
const ServicePricesPage = lazy(() => import('pages/service-prices'));
const TrafficSourcePage = lazy(() => import('pages/traffic-source'));
const PrinterOverViewPage = lazy(() => import('pages/printer-overview'));
const StandAlong = lazy(() => import('pages/stand-along'));
const NewsAndArticles = lazy(() => import('pages/news-and-articles'));
const SalesOffersPage = lazy(() => import('pages/sales-offers'));
const NewVehicleSettings = lazy(() => import('pages/new-vehicle-settings'));
const VehicleInspectionPage = lazy(() => import('pages/vehicle-inspection-page'));
const BlogPage = lazy(() => import('pages/blog'));
const AttendanceLogPage = lazy(() => import('pages/attendance-log'));
const DealsPage = lazy(() => import('pages/deals'));
const EnquiryManagerPage = lazy(() => import('pages/enquiry-manager'));
const LeadsPage = lazy(() => import('pages/leads'));
const TestDriveLogPage = lazy(() => import('pages/test-drive-log'));
const VehicleStockPage = lazy(() => import('pages/vehicle-stock'));
const VideoLogPage = lazy(() => import('pages/video-log'));
const TeamPerformancePage = lazy(() => import('pages/team-performance'));
const DocumentLogPage = lazy(() => import('pages/document-log'));
const BranchesPage = lazy(() => import('pages/branches'));
const FAQPage = lazy(() => import('pages/faq'));
const NewCarsVideos = lazy(() => import('pages/new-cars-videos'));
const PerformanceReviewPage = lazy(() => import('pages/performance-review'));
const WorkshopLoadingPage = lazy(() => import('pages/workshop-loading'));
const NotificationTypesPage = lazy(() => import('pages/notification-types'));
const CollectionAndDeliveryPage = lazy(() => import('pages/collection-and-delivery'));
const CollectionAndDeliveryDetailsPage = lazy(() => import('pages/collection-and-delivery-details'));
const DigitalWipsPage = lazy(() => import('pages/digital-wips'));
const DigitalWipPage = lazy(() => import('pages/digital-wip'));
const WorkshopLogPage = lazy(() => import('pages/workshop-log'));
const WorkshopLogRecordPage = lazy(() => import('pages/workshop-log-record'));
const ServiceBookingLogPage = lazy(() => import('pages/service-booking-log'));
const ServiceBookingLogCreatePage = lazy(() => import('pages/service-booking-log-create'));
const ServiceBookingPage = lazy(() => import('pages/service-booking'));
const WipAppointmentPreparationLogPage = lazy(() => import('pages/wip-appointment-preparation-log'));
const WipAppointmentPreparationPage = lazy(() => import('pages/wip-appointment-preparation'));
const BuildDealEditPage = lazy(() => import('pages/build-deal-edit'));
const ProductPresentationsPage = lazy(() => import('pages/product-presentations'));
const LoanVehiclesPage = lazy(() => import('pages/loan-vehicles').then((m) => ({ default: m.LoanVehiclesPage })));

const PrivateRoutes = () => {
  return (
    <Routes>
      <Route path="public/*" element={<PublicRoutes />} />
      <Route element={<EmptyLayout />}>
        <Route
          path="stand-along/*"
          element={
            <SuspensedView>
              <StandAlong />
            </SuspensedView>
          }
        />
      </Route>
      <Route
        path="digital-wip-view/:id/*"
        element={
          <SuspensedView>
            <DigitalWipPage />
          </SuspensedView>
        }
      />
      <Route element={<MasterLayout />}>
        {/* Redirect to Dashboard after success login/registartion */}
        {/* <Route path="auth/*" element={<Navigate to="/dashboard" />} /> */}
        {/* Pages */}
        <Route
          path="dashboard"
          element={
            <SuspensedView>
              <DashboardWrapper />
            </SuspensedView>
          }
        />

        <Route
          path="working-hours-tracking"
          element={
            <SuspensedView>
              <WorkingHoursTrackingWrapper />
            </SuspensedView>
          }
        />

        <Route
          path="employees-management/*"
          element={
            <SuspensedView>
              <EmployeesPage />
            </SuspensedView>
          }
        />

        <Route
          path="employees-settings/*"
          element={
            <SuspensedView>
              <EmployeesSettingsPage />
            </SuspensedView>
          }
        />

        <Route
          path="holidays/*"
          element={
            <SuspensedView>
              <HolidaysPage />
            </SuspensedView>
          }
        />

        <Route
          path="performance-review/*"
          element={
            <SuspensedView>
              <PerformanceReviewPage />
            </SuspensedView>
          }
        />

        <Route
          path="notification-types/*"
          element={
            <SuspensedView>
              <NotificationTypesPage />
            </SuspensedView>
          }
        />

        <Route
          path="workshop-loading/*"
          element={
            <SuspensedView>
              <WorkshopLoadingPage />
            </SuspensedView>
          }
        />

        <Route
          path="attendance-log/*"
          element={
            <SuspensedView>
              <AttendanceLogPage />
            </SuspensedView>
          }
        />
        <Route
          path="purchase-planner/*"
          element={
            <SuspensedView>
              <PurchasePlannerPage />
            </SuspensedView>
          }
        />
        <Route
          path="stock-state/*"
          element={
            <SuspensedView>
              <StockStatePage />
            </SuspensedView>
          }
        />
        <Route
          path="sales-perfomance/*"
          element={
            <SuspensedView>
              <SalesPerfomancePage />
            </SuspensedView>
          }
        />
        <Route
          path="team-performance/*"
          element={
            <SuspensedView>
              <TeamPerformancePage />
            </SuspensedView>
          }
        />

        <Route
          path="technicians-reports/*"
          element={
            <SuspensedView>
              <TechniciansReportsPage />
            </SuspensedView>
          }
        />
        <Route
          path="maintenance-tasks/*"
          element={
            <SuspensedView>
              <MaintenanceTasksPage />
            </SuspensedView>
          }
        />
        <Route
          path="vehicle-accident-log/*"
          element={
            <SuspensedView>
              <VehicleAccidentLogPage />
            </SuspensedView>
          }
        />
        <Route
          path="training-plans/*"
          element={
            <SuspensedView>
              <TrainingPlansPage />
            </SuspensedView>
          }
        />
        <Route
          path="risk-assessment/*"
          element={
            <SuspensedView>
              <RiskAssessmentPage />
            </SuspensedView>
          }
        />
        <Route
          path="printer-overview/*"
          element={
            <SuspensedView>
              <PrinterOverViewPage />
            </SuspensedView>
          }
        />
        <Route
          path="service-prices/*"
          element={
            <SuspensedView>
              <ServicePricesPage />
            </SuspensedView>
          }
        />
        <Route
          path="traffic-source/*"
          element={
            <SuspensedView>
              <TrafficSourcePage />
            </SuspensedView>
          }
        />

        <Route
          path="advisors-commissions/*"
          element={
            <SuspensedView>
              <AdvisorsCommissionsPage />
            </SuspensedView>
          }
        />

        <Route
          path="service-advisors/*"
          element={
            <SuspensedView>
              <ServiceAdvisorsPage />
            </SuspensedView>
          }
        />

        <Route
          path="deals/*"
          element={
            <SuspensedView>
              <DealsPage />
            </SuspensedView>
          }
        />

        <Route
          path="enquiry-manager/*"
          element={
            <SuspensedView>
              <EnquiryManagerPage />
            </SuspensedView>
          }
        />

        <Route
          path="leads/*"
          element={
            <SuspensedView>
              <LeadsPage />
            </SuspensedView>
          }
        />
        <Route
          path="vehicle-stock/*"
          element={
            <SuspensedView>
              <VehicleStockPage />
            </SuspensedView>
          }
        />

        <Route
          path="test-drive-log/*"
          element={
            <SuspensedView>
              <TestDriveLogPage />
            </SuspensedView>
          }
        />

        <Route
          path="video-log/*"
          element={
            <SuspensedView>
              <VideoLogPage />
            </SuspensedView>
          }
        />

        <Route
          path="appointments/*"
          element={
            <SuspensedView>
              <AppointmentsPage />
            </SuspensedView>
          }
        />

        <Route
          path="appraisals/*"
          element={
            <SuspensedView>
              <AppraisalsPage />
            </SuspensedView>
          }
        />

        <Route
          path="frequently-asking-questions/*"
          element={
            <SuspensedView>
              <FAQPage />
            </SuspensedView>
          }
        />

        <Route
          path="new-cars-videos/*"
          element={
            <SuspensedView>
              <NewCarsVideos />
            </SuspensedView>
          }
        />

        <Route
          path="branches/*"
          element={
            <SuspensedView>
              <BranchesPage />
            </SuspensedView>
          }
        />

        <Route
          path="documents-log/*"
          element={
            <SuspensedView>
              <DocumentLogPage />
            </SuspensedView>
          }
        />

        <Route
          path="site-settings/*"
          element={
            <SuspensedView>
              <SiteSettingsPage />
            </SuspensedView>
          }
        />

        <Route
          path="ai-settings/*"
          element={
            <SuspensedView>
              <AiSettingsPage />
            </SuspensedView>
          }
        />
        <Route
          path="news-and-articles/*"
          element={
            <SuspensedView>
              <NewsAndArticles />
            </SuspensedView>
          }
        />
        <Route
          path="events-and-offers/*"
          element={
            <SuspensedView>
              <SalesOffersPage />
            </SuspensedView>
          }
        />
        <Route
          path="new-vehicle-settings/*"
          element={
            <SuspensedView>
              <NewVehicleSettings />
            </SuspensedView>
          }
        />
        <Route
          path="jobs/*"
          element={
            <SuspensedView>
              <JobsPage />
            </SuspensedView>
          }
        />

        <Route
          path="vehicle-inspection/*"
          element={
            <SuspensedView>
              <VehicleInspectionPage />
            </SuspensedView>
          }
        />

        <Route
          path="vehicle-preparation/log/*"
          element={
            <SuspensedView>
              <VehiclePreparationLogPage />
            </SuspensedView>
          }
        />

        <Route
          path="vehicle-preparation/check/:id/*"
          element={
            <SuspensedView>
              <EditVehiclePreparationCheckPage />
            </SuspensedView>
          }
        />

        <Route
          path="vehicle-preparation/check"
          element={
            <SuspensedView>
              <VehiclePreparationCheckPage />
            </SuspensedView>
          }
        />

        <Route
          path="deferred-work/details/:id/*"
          element={
            <SuspensedView>
              <DeferredWorkDetailsPage />
            </SuspensedView>
          }
        />

        <Route
          path="deferred-work"
          element={
            <SuspensedView>
              <DeferredWorkPage />
            </SuspensedView>
          }
        />

        <Route
          path="workshop-day-planner/:idParam?/*"
          element={
            <SuspensedView>
              <WorkshopPlannerPage />
            </SuspensedView>
          }
        />

        <Route
          path="workshop-check-in/log/*"
          element={
            <SuspensedView>
              <WorkshopCheckInLogPage />
            </SuspensedView>
          }
        />

        <Route
          path="workshop-check-out/log/*"
          element={
            <SuspensedView>
              <WorkshopCheckOutLogPage />
            </SuspensedView>
          }
        />

        <Route
          path="complaint-log/:id"
          element={
            <SuspensedView>
              <ComplaintLogDetailsPage />
            </SuspensedView>
          }
        />

        <Route
          path="complaint-log/*"
          element={
            <SuspensedView>
              <ComplaintLogPage />
            </SuspensedView>
          }
        />

        <Route
          path="workshop-check-out/edit/:idParam/*"
          element={
            <SuspensedView>
              <WorkshopCheckOutPage />
            </SuspensedView>
          }
        />

        <Route
          path="workshop-check-out/preview/:idParam/*"
          element={
            <SuspensedView>
              <WorkshopCheckOutPreviewPage />
            </SuspensedView>
          }
        />

        <Route
          path="loan-vehicles/*"
          element={
            <SuspensedView>
              <LoanVehiclesPage />
            </SuspensedView>
          }
        />

        <Route
          path="workshop-check-in/new/*"
          element={
            <SuspensedView>
              <WorkshopCheckInLookUpPage />
            </SuspensedView>
          }
        />

        <Route
          path="workshop-check-in/new/:idParam/*"
          element={
            <SuspensedView>
              <WorkshopCheckInNewPage isEdit={false} />
            </SuspensedView>
          }
        />

        <Route
          path="workshop-check-in/edit/:idParam/*"
          element={
            <SuspensedView>
              <WorkshopCheckInNewPage isEdit={true} />
            </SuspensedView>
          }
        />

        <Route
          path="workshop-check-in/view/:idParam/*"
          element={
            <SuspensedView>
              <WorkshopCheckInViewPage />
            </SuspensedView>
          }
        />

        <Route
          path="blog/*"
          element={
            <SuspensedView>
              <BlogPage />
            </SuspensedView>
          }
        />

        <Route
          path="vhc/record/*"
          element={
            <SuspensedView>
              <VHCPage />
            </SuspensedView>
          }
        />

        <Route
          path="vhc/statistics/*"
          element={
            <SuspensedView>
              <VhcStatistics />
            </SuspensedView>
          }
        />

        <Route
          path="vhc/quote-stats/*"
          element={
            <SuspensedView>
              <VhcQuoteStats />
            </SuspensedView>
          }
        />

        <Route
          path="recalls/edit/:id/*"
          element={
            <SuspensedView>
              <RecallEditPage />
            </SuspensedView>
          }
        />

        <Route
          path="recalls/create/*"
          element={
            <SuspensedView>
              <RecallCreatePage />
            </SuspensedView>
          }
        />

        <Route
          path="recalls/*"
          element={
            <SuspensedView>
              <RecallsPage />
            </SuspensedView>
          }
        />

        <Route
          path="collection-and-delivery/:id/*"
          element={
            <SuspensedView>
              <CollectionAndDeliveryDetailsPage />
            </SuspensedView>
          }
        />

        <Route
          path="collection-and-delivery/*"
          element={
            <SuspensedView>
              <CollectionAndDeliveryPage />
            </SuspensedView>
          }
        />

        <Route
          path="digital-wip/*"
          element={
            <SuspensedView>
              <DigitalWipsPage />
            </SuspensedView>
          }
        />

        <Route
          path="digital-wip/:id/*"
          element={
            <SuspensedView>
              <DigitalWipPage />
            </SuspensedView>
          }
        />

        <Route
          path="workshop-log/*"
          element={
            <SuspensedView>
              <WorkshopLogPage />
            </SuspensedView>
          }
        />

        <Route
          path="workshop-log/:id/*"
          element={
            <SuspensedView>
              <WorkshopLogRecordPage />
            </SuspensedView>
          }
        />

        <Route
          path="service-booking-log/create/*"
          element={
            <SuspensedView>
              <ServiceBookingLogCreatePage />
            </SuspensedView>
          }
        />

        <Route
          path="service-booking-log/*"
          element={
            <SuspensedView>
              <ServiceBookingLogPage />
            </SuspensedView>
          }
        />

        <Route
          path="service-booking/:id/*"
          element={
            <SuspensedView>
              <ServiceBookingPage />
            </SuspensedView>
          }
        />

        <Route
          path="wip-appointment-preparation/*"
          element={
            <SuspensedView>
              <WipAppointmentPreparationLogPage />
            </SuspensedView>
          }
        />

        <Route
          path="wip-appointment-preparation/:id/*"
          element={
            <SuspensedView>
              <WipAppointmentPreparationPage />
            </SuspensedView>
          }
        />

        <Route
          path="build-deal/:id/*"
          element={
            <SuspensedView>
              <BuildDealEditPage />
            </SuspensedView>
          }
        />

        <Route
          path="warranty-logs/*"
          element={
            <SuspensedView>
              <WarrantyLogsPage />
            </SuspensedView>
          }
        />

        <Route
          path="product-presentations/*"
          element={
            <SuspensedView>
              <ProductPresentationsPage />
            </SuspensedView>
          }
        />

        {/* Page Not Found */}
        <Route path="*" element={<Navigate to="/error/404" />} />
      </Route>
    </Routes>
  );
};

export { PrivateRoutes };
